import { useEffect, useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import './PageStyle.css';
import ApiSender from '../common/ApiLogic.js';

const NATIONAL_PREFIXES = ['50', '52', '54', '55', '56', '58'];

function UnsubPage() {
    const apiSender = new ApiSender();
    const location = useLocation();
    const [language, setLanguage] = useState('AR');
    const [msisdn, setMsisdn] = useState('');
    const [msisdnEnglish, setMsisdnEnglish] = useState('');
    const [warning, setWarning] = useState(false);
    const [message, setMessage] = useState(null);
    const [arMessage, setArMessage] = useState(null);
    const [enMessage, setEnMessage] = useState(null);
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const paramsObject = useMemo(() => {
        const obj = {};
        for (const [key, value] of params.entries()) {
            obj[key] = value;
        }
        return obj;
    }, [params]);
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        const rawLang = paramsObject?.lang?.toUpperCase();
        const upperLang = rawLang === 'EN' || rawLang === 'AR' ? rawLang : language;
        setLanguage(upperLang);

        const menuToggle = document.getElementById('menuToggle');
        const sidebar = document.getElementById('sidebar');
        const closeSidebar = document.getElementById('closeSidebar');

        if (menuToggle && sidebar && closeSidebar) {
            const openSidebar = () => sidebar.classList.add('open');
            const closeSide = () => sidebar.classList.remove('open');

            menuToggle.addEventListener('click', openSidebar);
            closeSidebar.addEventListener('click', closeSide);

            return () => {
                menuToggle.removeEventListener('click', openSidebar);
                closeSidebar.removeEventListener('click', closeSide);
            };
        }
    }, []);

    useEffect(() => {
        setMessage(language === 'EN' ? enMessage : arMessage);
    }, [language]);

    const handleLanguage = (value) => {
        setLanguage(value);
    };

    const convertDigits = (input) => input
        .replace(/[٠-٩]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
        .replace(/[^\d]/g, '');

    const isMsisdnValid = (m) =>
        typeof m === 'string' &&
        ((m.length === 9 && NATIONAL_PREFIXES.some(p => m.startsWith(p))) ||
            (m.length === 10 && NATIONAL_PREFIXES.some(p => m.startsWith('0' + p))));

    const formatMsisdn = (value) => {
        let digits = value.replace(/[^\d٠-٩]/g, '');

        if (!digits.startsWith('5') && !digits.startsWith('0') && !digits.startsWith('٠') && !digits.startsWith('٥')) return '';

        if ((digits.startsWith('0') && digits.charAt(1) && digits.charAt(1) !== '5')
            || (digits.startsWith('٠') && digits.charAt(1) && digits.charAt(1) !== '٥')) {
            digits = digits.charAt(0) + digits.slice(2);
        }

        let part1 = digits.slice(0, 1);
        let part2 = digits.slice(1, 5);
        let part3 = digits.slice(5, 9);
        if (digits.startsWith('05') || digits.startsWith('٠٥')) {
            part1 = digits.slice(0, 2);
            part2 = digits.slice(2, 6);
            part3 = digits.slice(6, 10);
        }

        let formatted = part1;
        if (part2) formatted += ' ' + part2;
        if (part3) formatted += ' ' + part3;

        return formatted;
    };

    const handleMsisdnChange = (event) => {
        const newMsisdn = event.target.value;
        setMsisdn(formatMsisdn(newMsisdn));
        const convertMsisdn = convertDigits(newMsisdn);
        setMsisdnEnglish(convertMsisdn);
        setWarning(false);
        setMessage('');
        setArMessage('');
        setEnMessage('');
        if (!isMsisdnValid(convertMsisdn)) {
            document.getElementById('checkmark-container-msisdn').style.opacity = 0;
            document.getElementById('unsub-btn').classList.remove('active');
            return;
        }
        document.getElementById('checkmark-container-msisdn').style.opacity = 1;
        document.getElementById('unsub-btn').classList.add('active');
    };

    const showUnsubscribeConfirm = () => {
        if (!isMsisdnValid(msisdnEnglish)) {
            setMsisdn('');
            setMsisdnEnglish('');
            setMessage(language === 'EN' ? 'Incorrect Phone Number Format' : 'رقم غير صحيح');
            setWarning(true);
            return;
        }
        setShowPopup(true);
    };

    const confirmUnsubscribe = async () => {
        setWarning(false);
        setMessage('');
        setArMessage('');
        setEnMessage('');

        document.getElementById('loading-spinner-unsub-btn').style.display = 'inline-flex';
        document.getElementById('unsub-confirm-btn').disabled = true;
        document.getElementById('unsub-cancel-btn').disabled = true;
        const result = await apiSender.unsub(msisdnEnglish);
        document.getElementById('loading-spinner-unsub-btn').style.display = 'none';
        document.getElementById('unsub-confirm-btn').disabled = false;
        document.getElementById('unsub-cancel-btn').disabled = false;
        setShowPopup(false);
        setWarning(true);
        setArMessage(result.arMessage);
        setEnMessage(result.message);
        setMessage(language === 'EN' ? result.message : result.arMessage);
    };

    const cancelUnsubscribe = () => {
        setShowPopup(false);
    };

    return (
        <div className="App">
            <div className='top'>
                <div className='bar'>
                    <div className="bar-left">
                        <img className="logo" src="/logo.png" alt="CoreFlex"></img>
                    </div>
                    <div className="bar-right">
                        <div className='language'>
                            <button className={language === 'EN' ? 'active' : ''} onClick={() => handleLanguage('EN')}>EN</button>
                            <button className={language === 'AR' ? 'active' : ''} onClick={() => handleLanguage('AR')}>AR</button>
                        </div>
                        <div className="menu-label" id="menuToggle">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="sidebar" id="sidebar">
                <div className="close-btn" id="closeSidebar">&times;</div>
                <ul className="menu-items">
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/terms?lang=${language}`}>{language === 'EN' ? 'Terms & Conditions' : 'الأحكام والشروط'}</Link></li>
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/privacy?lang=${language}`}>{language === 'EN' ? 'Privacy Policy' : 'سياسة الخصوصية'}</Link></li>
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'} onClick={() => window.location.href = 'mailto:support@coreflex.fun'}>{language === 'EN' ? 'Contact us' : 'معلومات الاتصال'}</li>
                </ul>
            </div>
            <div className='middle'>
                <div className='title' dir={language === 'EN' ? 'ltr' : 'rtl'}>{language === 'EN' ? 'Enjoy using the mobile app for all types of fitness workouts' : 'استمتع باستخدام تطبيق الهاتف لجميع أنواع تمارين اللياقة البدنية'}</div>
                <div className='input-content'>
                    <div className='title' dir={language === 'EN' ? 'ltr' : 'rtl'}><p>{language === 'EN' ? 'Enter your mobile number to unsubscribe' : 'أدخل رقم هاتفك المحمول لالغاء الاشتراك'}</p></div>
                    <div id="msisdn-input" className='phoneInputWrap'>
                        <div className="phone-input">
                            <div className="phone"></div>
                            <input type="tel" value={msisdn} autoComplete="off" id="phone-input" name="phone-input-name" placeholder={language === 'EN' ? '(0)5 XXXX XXXX' : '(٠)٥ xxxx xxxx'} minLength={9} maxLength={msisdn.startsWith('5') || msisdn.startsWith('٥') ? 11 : 12} className="text-input" autoFocus onChange={handleMsisdnChange} />
                            <div id="checkmark-container-msisdn" className="checkmark-container" data-checkmark-container="true" style={{ opacity: 0 }}>
                                <svg id="success-check-mark-msisdn" className="checkmark__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                    <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"></circle>
                                    <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className='button'>
                        <button id='unsub-btn' onClick={showUnsubscribeConfirm}>{language === 'EN' ? 'UNSUBSCRIBE' : 'الغاء اشتراك'}</button>
                    </div>

                    <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`} dir={language === 'EN' ? 'ltr' : 'rtl'}>
                        <span id='warning-msg'>{message}</span>
                    </div>

                    {showPopup && (
                        <div className="popup">
                            <div className="popup-content" dir={language === 'EN' ? 'ltr' : 'rtl'}>
                                <p>{language === 'EN' ? 'Are you sure you want to unsubscribe from CoreFlex?' : 'هل أنت متأكد أنك تريد إلغاء الاشتراك في CoreFlex؟'}</p>
                                <button id='unsub-confirm-btn' style={{ display: 'block' }} onClick={confirmUnsubscribe}>
                                    {language === 'EN' ? 'Confirm' : 'تاكيد'}
                                    <div id="loading-spinner-unsub-btn" className="spinner"></div>
                                </button>
                                <button id="unsub-cancel-btn" style={{ display: 'block' }} onClick={cancelUnsubscribe}>{language === 'EN' ? 'Cancel' : 'الغاء'}</button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
            <div className='bottom'>
                <ul className='links'>
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/terms?lang=${language}`}>{language === 'EN' ? 'Terms & Conditions' : 'الأحكام والشروط'}</Link></li>
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'}><Link to={`/privacy?lang=${language}`}>{language === 'EN' ? 'Privacy Policy' : 'سياسة الخصوصية'}</Link></li>
                    <li dir={language === 'EN' ? 'ltr' : 'rtl'}><a href='mailto:support@coreflex.fun'>{language === 'EN' ? 'Contact Us' : 'معلومات الاتصال'}</a></li>
                </ul>
            </div>
        </div>
    );
}

export default UnsubPage;
